import React, { useEffect, useState } from "react";
import { Alert, AlertTitle, Box, Chip, Paper, Typography } from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import PaidIcon from "@mui/icons-material/Paid";
import BoltIcon from "@mui/icons-material/Bolt";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ContentShell from "components/ContentShell";
import CustomButton from "components/CustomButton";
import FilePicker from "components/FilePicker";
import ImagePreviewBox from "components/ImagePreviewBox";
import { useSnackbar } from "hooks/useSnackbar";
import {
  deleteChallengeRewardImage,
  getChallengeRewardImages,
  upsertChallengeRewardImage,
} from "services/challengeService";
import { uploadImageV2 } from "services/uploadService";
import { DEFAULT_IMAGE_DIMENSIONS, ImageType } from "utils/imageConstants";

// Must stay in sync with ChallengeRewardType in the game backend.
const REWARD_TYPES: {
  id: string;
  label: string;
  accent: string;
  icon: React.ReactNode;
}[] = [
  { id: "PBF_COINS_SMALL", label: "PBF Coins (Small)", accent: "#f59e0b", icon: <PaidIcon /> },
  { id: "PBF_COINS_MEDIUM", label: "PBF Coins (Medium)", accent: "#f97316", icon: <PaidIcon /> },
  { id: "PBF_COINS_LARGE", label: "PBF Coins (Large)", accent: "#d97706", icon: <PaidIcon /> },
  { id: "XP", label: "XP", accent: "#7c3aed", icon: <BoltIcon /> },
  { id: "PACK", label: "Pack", accent: "#2563eb", icon: <Inventory2Icon /> },
  { id: "MYSTERY_PLAYER_CARD", label: "Mystery Player Card", accent: "#0d9488", icon: <AutoAwesomeIcon /> },
];

const REWARD_DIMS = DEFAULT_IMAGE_DIMENSIONS[ImageType.CHALLENGE_REWARD_IMAGE];

interface RewardImage {
  rewardType: string;
  imagePath?: string;
  imageUrl?: string;
}

interface RewardTypeMeta {
  id: string;
  label: string;
  accent: string;
  icon: React.ReactNode;
}

// One reward-type card. Keeps its own pending file, dimensions and live preview.
const RewardImageCard = ({
  rt,
  current,
  onUpload,
  onRemove,
}: {
  rt: RewardTypeMeta;
  current?: RewardImage;
  onUpload: (rewardType: string, file: File, dims: any) => Promise<void>;
  onRemove: (rewardType: string) => Promise<void>;
}) => {
  const { showSnackbar } = useSnackbar();
  const [file, setFile] = useState<File | null>(null);
  const [dims, setDims] = useState<any>(null);
  const [previewUrl, setPreviewUrl] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);

  const hasImage = !!current?.imageUrl;

  // Live object-URL preview of the selected file (cleaned up on change/unmount).
  useEffect(() => {
    if (!file) {
      setPreviewUrl(undefined);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const handleUpload = async () => {
    if (!file) return;
    setBusy(true);
    try {
      await onUpload(rt.id, file, dims);
      showSnackbar(`Default image saved for ${rt.label}.`, "success");
      setFile(null);
      setDims(null);
    } catch (error: any) {
      showSnackbar(error.message || "Failed to save reward image", "error");
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async () => {
    if (!window.confirm(`Remove the default image for ${rt.label}?`)) return;
    setBusy(true);
    try {
      await onRemove(rt.id);
      showSnackbar(`Default image removed for ${rt.label}.`, "success");
    } catch (error: any) {
      showSnackbar(error.message || "Failed to remove reward image", "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        border: "1px solid",
        borderColor: hasImage ? `${rt.accent}55` : "divider",
        overflow: "hidden",
        transition: "border-color 0.3s, box-shadow 0.3s",
        "&:hover": { boxShadow: `0 4px 20px ${rt.accent}1f` },
      }}
    >
      {/* Accent header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: 2.5,
          py: 1.5,
          borderBottom: "1px solid",
          borderBottomColor: "divider",
          borderLeft: `4px solid ${rt.accent}`,
          background: `linear-gradient(135deg, ${rt.accent}12 0%, transparent 100%)`,
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
            background: `linear-gradient(135deg, ${rt.accent}, ${rt.accent}cc)`,
            "& svg": { fontSize: 20 },
            flexShrink: 0,
          }}
        >
          {rt.icon}
        </Box>
        <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1, lineHeight: 1.2 }}>
          {rt.label}
        </Typography>
        <Chip
          size="small"
          label={hasImage ? "Configured" : "Missing"}
          color={hasImage ? "success" : "warning"}
          variant={hasImage ? "filled" : "outlined"}
          sx={{ fontWeight: 600 }}
        />
      </Box>

      {/* Body — previews on the left, upload controls on the right */}
      <Box sx={{ p: 2.5, display: "flex", gap: 3, flexWrap: "wrap", alignItems: "flex-start" }}>
        {/* Previews */}
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          <Box sx={{ width: 200 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5, fontWeight: 600 }}>
              Current default
            </Typography>
            <ImagePreviewBox
              url={current?.imageUrl}
              alt={rt.label}
              height={210}
              badge={hasImage ? <Chip size="small" label="Default" /> : undefined}
              emptyIcon={<ImageOutlinedIcon fontSize="large" />}
              emptyText="No default image yet"
            />
          </Box>

          {previewUrl && (
            <Box sx={{ width: 200 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5, fontWeight: 600 }}>
                New image (preview)
              </Typography>
              <ImagePreviewBox
                url={previewUrl}
                alt="New reward image"
                height={210}
                badge={<Chip size="small" color="primary" label="New" />}
              />
            </Box>
          )}
        </Box>

        {/* Upload controls */}
        <Box sx={{ flex: 1, minWidth: 300 }}>
          <FilePicker
            // Remount (reset internal file + dimension state) after each save.
            key={`reward-image-${rt.id}-${current?.imageUrl ?? "empty"}`}
            label="Choose image"
            name={`reward-image-${rt.id}`}
            fileType="image/*"
            showPreview={false}
            imageType={ImageType.CHALLENGE_REWARD_IMAGE}
            enableDimensionControl={true}
            metaData={{
              note: `Recommended: ${REWARD_DIMS.width}×${REWARD_DIMS.height}px. Resized to WebP. The image keeps its aspect ratio — it is not cropped.`,
            }}
            onFileSelect={(f: any, d?: any) => {
              setFile(f || null);
              setDims(d ?? null);
            }}
          />

          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 2 }}>
            <CustomButton
              startIcon={<CloudUploadIcon />}
              disabled={!file || busy}
              onClick={handleUpload}
            >
              {busy ? "Saving..." : hasImage ? "Replace" : "Upload"}
            </CustomButton>
            {hasImage && (
              <CustomButton startIcon={<DeleteOutlineIcon />} disabled={busy} onClick={handleRemove}>
                Remove
              </CustomButton>
            )}
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

const ChallengeRewardImages = () => {
  const { showSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(false);
  const [imagesByType, setImagesByType] = useState<Record<string, RewardImage>>({});

  const fetchImages = async () => {
    try {
      setLoading(true);
      const resp = await getChallengeRewardImages();
      // The S3 key is deterministic, so a replaced image reuses the same URL and the browser
      // would serve the cached old one. Append a cache-buster per fetch to force the latest.
      const stamp = Date.now();
      const map: Record<string, RewardImage> = {};
      (resp?.data || []).forEach((item: RewardImage) => {
        map[item.rewardType] = {
          ...item,
          imageUrl: item.imageUrl ? `${item.imageUrl.split("?")[0]}?t=${stamp}` : item.imageUrl,
        };
      });
      setImagesByType(map);
    } catch (error: any) {
      showSnackbar(error.message || "Failed to load reward images", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchImages();
  }, []);

  // Resize to the dimensions picked in the control (default = image's own size),
  // so the aspect ratio is preserved and the image is never cropped.
  const uploadImage = async (rewardType: string, file: File, dims: any) => {
    const upsertResp = await upsertChallengeRewardImage({ rewardType, fileType: "image/webp" });
    const uploadUrl = upsertResp?.data?.uploadImageUrl;
    if (!uploadUrl) throw new Error("Could not obtain an upload URL.");

    const uploadResp = await uploadImageV2({
      data: file,
      uploadUrl,
      resize: true,
      resizeToWidth: dims?.targetWidth || REWARD_DIMS.width,
      resizeToHeight: dims?.targetHeight || REWARD_DIMS.height,
    });
    if (!uploadResp?.status) {
      throw new Error(uploadResp?.message || "Image upload failed.");
    }
    await fetchImages();
  };

  const removeImage = async (rewardType: string) => {
    await deleteChallengeRewardImage(rewardType);
    await fetchImages();
  };

  const configuredCount = REWARD_TYPES.filter((rt) => imagesByType[rt.id]?.imageUrl).length;
  const missingCount = REWARD_TYPES.length - configuredCount;

  return (
    <ContentShell title="Challenge Reward Images" loading={loading}>
      {/* Intro / summary banner */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mb: 3,
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          background: "linear-gradient(135deg, #1976d20a 0%, transparent 60%)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
          <ImageOutlinedIcon sx={{ color: "primary.main" }} />
          <Typography variant="subtitle1" fontWeight={700}>
            Default reward images
          </Typography>
          <Chip
            size="small"
            label={`${configuredCount}/${REWARD_TYPES.length} configured`}
            color={missingCount ? "warning" : "success"}
            sx={{ ml: "auto", fontWeight: 600 }}
          />
        </Box>
        <Typography variant="body2" color="text.secondary">
          Configure one default image per reward type. When a challenge is created
          — in the single-create form or via bulk upload — it inherits the default
          image of its reward type. A custom image uploaded on an individual
          challenge always overrides this default. For <b>Pack</b> and{" "}
          <b>Mystery Player Card</b> rewards with no custom image, the reward pack's
          own image is shown in-game and this default is used only as a final
          fallback.
        </Typography>
        {missingCount > 0 && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            <AlertTitle sx={{ fontWeight: 700 }}>
              {missingCount} reward type(s) have no default image
            </AlertTitle>
            Bulk uploads that use those reward types will fail until a default
            image is configured here.
          </Alert>
        )}
      </Paper>

      {/* Reward-type cards */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
        {REWARD_TYPES.map((rt) => (
          <RewardImageCard
            key={rt.id}
            rt={rt}
            current={imagesByType[rt.id]}
            onUpload={uploadImage}
            onRemove={removeImage}
          />
        ))}
      </Box>
    </ContentShell>
  );
};

export default ChallengeRewardImages;
